/**
 * @fileoverview Connection-quality monitor — classifies link health from
 * measured round-trip samples, browser connection hints, and transport failure
 * streaks.
 * @module local-db/services/connectionQuality
 *
 * Responsibilities:
 * - Maintain a rolling window of round-trip latency samples (HTTP sync calls
 *   and WebSocket heartbeat pongs).
 * - Read `navigator.connection` hints (effectiveType, rtt, downlink, saveData)
 *   where the browser exposes them (Chromium/Android).
 * - Classify link quality as 'excellent' | 'good' | 'poor' | 'offline' and
 *   notify subscribers only when the classification changes.
 * - Provide `timedFetch` — a drop-in fetch wrapper that records each request's
 *   latency or failure into the sample window.
 *
 * Dependencies:
 * - Browser APIs only (`navigator.onLine`, `navigator.connection`, `fetch`,
 *   window events). No local-db imports — this module must stay dependency-free
 *   so producers (replication, WebSocket, api clients) and the sync status
 *   store can all consume it without cycles.
 *
 * Architecture:
 * - Source of truth for *link* quality only. Overall sync health
 *   (healthStatus / syncQuality) is computed by syncStatusStore, which folds
 *   linkQuality in alongside queue depth, errors, and connectivity.
 * - Signals are layered, worst-signal-wins:
 *     1. `navigator.onLine` — hard 'offline' floor.
 *     2. `navigator.connection.effectiveType` — coarse hint floor
 *        ('slow-2g'/'2g' → poor, '3g' → good). Absent on non-Chromium browsers.
 *     3. Measured RTT median over the rolling sample window — the truthful
 *        signal: reflects real app traffic, not browser estimates.
 *     4. Recent transport failures (fetch rejects / timeouts) — poor floor.
 * - 'offline' here means "browser reports no network". A live browser with a
 *   dead sync socket is handled by syncStatusStore's isConnected check —
 *   quality simply stops receiving samples and reconnect pressure surfaces
 *   through the store instead.
 *
 * Notes:
 * - Fail-open: with no samples and no hints the quality is 'excellent' — we
 *   never alarm without evidence.
 * - Deliberately no adaptive pacing here yet. getLinkQuality() is the query
 *   seam a future throttle (smaller pull batches, slower heartbeat on poor
 *   links) will consume.
 * - Browser DevTools network throttling updates effectiveType AND slows real
 *   requests, so both signal classes respond to throttled testing.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * @contract LinkQuality
 * Purpose: One-word classification of the current connection's health.
 * Fields:
 * - 'excellent': no evidence of degradation (default; fail-open).
 * - 'good': usable but measurable latency — elevated RTT or '3g'-class hint.
 * - 'poor': degraded — high RTT, '2g'-class hint, reconnect/failure pressure.
 * - 'offline': browser reports no connectivity.
 * Rules: Derived, never set directly; worst active signal wins.
 * Sync: Flows into SyncStatus.linkQuality and the TetherSync widget badge.
 */
export type LinkQuality = 'excellent' | 'good' | 'poor' | 'offline';

/**
 * @contract LinkQualitySnapshot
 * Purpose: Display/debug detail behind the current LinkQuality.
 * Fields:
 * - quality: Current classification.
 * - medianRttMs: Median round-trip latency across live samples, or null when
 *   none have been recorded.
 * - effectiveType / downlinkMbps / saveData: navigator.connection hints, or
 *   null/false when the browser does not expose the Network Information API.
 * - recentFailureCount: Transport failures inside the failure window.
 * - sampleCount: Live samples contributing to medianRttMs.
 * Rules: Read-only; produced by getLinkQualitySnapshot().
 * Sync: In-memory only — no persistence, no server traffic.
 */
export interface LinkQualitySnapshot {
  quality: LinkQuality;
  medianRttMs: number | null;
  effectiveType: string | null;
  downlinkMbps: number | null;
  saveData: boolean;
  recentFailureCount: number;
  sampleCount: number;
}

type LinkQualityListener = (snapshot: LinkQualitySnapshot) => void;

// ---------------------------------------------------------------------------
// Tunables — single place to adjust classification behavior
// ---------------------------------------------------------------------------

/** Samples older than this are ignored (field conditions change fast). */
const SAMPLE_WINDOW_MS = 120_000;
/** Cap on retained samples so a burst of pushes cannot skew the median. */
const MAX_SAMPLES = 40;
/** Failure streaks are evaluated over this shorter window. */
const FAILURE_WINDOW_MS = 60_000;
/** Median RTT above this → 'poor'. ~1.5s round-trips stall interactive work. */
const RTT_POOR_MS = 1_500;
/** Median RTT above this → at most 'good'. */
const RTT_GOOD_MS = 600;
/** Transport failures inside FAILURE_WINDOW_MS → 'poor' floor. */
const FAILURES_POOR = 3;
/** Transport failures inside FAILURE_WINDOW_MS → 'good' floor. */
const FAILURES_GOOD = 1;

const QUALITY_RANK: Record<LinkQuality, number> = {
  excellent: 0,
  good: 1,
  poor: 2,
  offline: 3,
};

function worst(a: LinkQuality, b: LinkQuality): LinkQuality {
  return QUALITY_RANK[b] > QUALITY_RANK[a] ? b : a;
}

// ---------------------------------------------------------------------------
// Monitor
// ---------------------------------------------------------------------------

interface LatencySample {
  at: number;
  ms: number;
}

class ConnectionQualityMonitor {
  private samples: LatencySample[] = [];
  private failures: number[] = [];
  private listeners = new Set<LinkQualityListener>();
  private quality: LinkQuality = typeof navigator === 'undefined' || navigator.onLine
    ? 'excellent'
    : 'offline';
  private wired = false;

  /**
   * Idempotent browser-event wiring. Called lazily on first use so importing
   * this module in non-browser contexts (tests, SSR) is safe.
   */
  private ensureWired(): void {
    if (this.wired || typeof window === 'undefined') return;
    this.wired = true;
    window.addEventListener('online', () => this.setBrowserOnline(true));
    window.addEventListener('offline', () => this.setBrowserOnline(false));
    const conn = getConnectionInfo();
    if (conn && typeof conn.addEventListener === 'function') {
      conn.addEventListener('change', () => this.reevaluate());
    }
  }

  private setBrowserOnline(online: boolean): void {
    if (!online) {
      // Hard floor — nothing else matters while the browser says we're down.
      this.setQuality('offline');
    } else {
      this.reevaluate();
    }
  }

  /**
   * @process recordRequestSample
   * Steps: Append a round-trip measurement, prune stale samples, re-evaluate.
   * Inputs: ms — elapsed request time in milliseconds.
   * Outputs: Updated rolling window; subscribers notified on quality change.
   * Failure Handling: Non-positive/NaN samples are discarded.
   * Idempotency: NOT REQUIRED — each call records one sample.
   */
  recordRequestSample(ms: number): void {
    this.ensureWired();
    if (!Number.isFinite(ms) || ms < 0) return;
    this.samples.push({ at: Date.now(), ms });
    this.reevaluate();
  }

  /**
   * @process recordRequestFailure
   * Steps: Append a transport failure timestamp, prune, re-evaluate.
   * Inputs: None (call when fetch rejects / a request times out).
   * Outputs: Updated failure window; subscribers notified on quality change.
   * Failure Handling: n/a
   * Idempotency: NOT REQUIRED — each call records one failure.
   */
  recordRequestFailure(): void {
    this.ensureWired();
    this.failures.push(Date.now());
    this.reevaluate();
  }

  getSnapshot(): LinkQualitySnapshot {
    this.ensureWired();
    const now = Date.now();
    const live = this.liveSamples(now);
    return {
      quality: this.quality,
      medianRttMs: live.length ? median(live.map((s) => s.ms)) : null,
      effectiveType: getConnectionInfo()?.effectiveType ?? null,
      downlinkMbps: getConnectionInfo()?.downlink ?? null,
      saveData: getConnectionInfo()?.saveData ?? false,
      recentFailureCount: this.liveFailures(now).length,
      sampleCount: live.length,
    };
  }

  getQuality(): LinkQuality {
    this.ensureWired();
    return this.quality;
  }

  subscribe(listener: LinkQualityListener): () => void {
    this.ensureWired();
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Recompute classification from all signals. Called on every new sample,
   * failure, connection-change, and online/offline transition.
   *
   * @errorHandling reevaluate
   * Strategy: Missing signals degrade gracefully — no Network Information API
   * means hints contribute nothing; no samples means the measured tier is
   * 'excellent'. Classification never throws.
   * User Impact: Quality may read 'excellent' on non-Chromium browsers until
   * request samples accumulate.
   * Logging: None — state is observable via getSnapshot().
   */
  private reevaluate(): void {
    const now = Date.now();

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      this.setQuality('offline');
      return;
    }

    // Hint floor from navigator.connection (Chromium only).
    const conn = getConnectionInfo();
    let quality: LinkQuality = 'excellent';
    if (conn?.effectiveType === 'slow-2g' || conn?.effectiveType === '2g') {
      quality = 'poor';
    } else if (conn?.effectiveType === '3g' || (conn?.rtt ?? 0) >= RTT_GOOD_MS) {
      quality = 'good';
    }

    // Measured floor from the rolling RTT median.
    const live = this.liveSamples(now);
    if (live.length >= 3) {
      const med = median(live.map((s) => s.ms));
      if (med > RTT_POOR_MS) quality = worst(quality, 'poor');
      else if (med > RTT_GOOD_MS) quality = worst(quality, 'good');
    }

    // Failure-streak floor.
    const recentFailures = this.liveFailures(now).length;
    if (recentFailures >= FAILURES_POOR) quality = worst(quality, 'poor');
    else if (recentFailures >= FAILURES_GOOD) quality = worst(quality, 'good');

    this.setQuality(quality);
  }

  private liveSamples(now: number): LatencySample[] {
    this.samples = this.samples.filter((s) => now - s.at <= SAMPLE_WINDOW_MS);
    if (this.samples.length > MAX_SAMPLES) {
      this.samples = this.samples.slice(this.samples.length - MAX_SAMPLES);
    }
    return this.samples;
  }

  private liveFailures(now: number): number[] {
    this.failures = this.failures.filter((t) => now - t <= FAILURE_WINDOW_MS);
    return this.failures;
  }

  private setQuality(next: LinkQuality): void {
    if (next === this.quality) return;
    this.quality = next;
    const snapshot = this.getSnapshot();
    this.listeners.forEach((listener) => listener(snapshot));
  }

  /** Test hook — clears all accumulated state. */
  resetForTests(): void {
    this.samples = [];
    this.failures = [];
    this.quality = typeof navigator === 'undefined' || navigator.onLine ? 'excellent' : 'offline';
  }
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

/** Minimal subset of the Network Information API we consume. */
interface ConnectionInfoLike {
  effectiveType?: string;
  rtt?: number;
  downlink?: number;
  saveData?: boolean;
  addEventListener?: (type: string, listener: () => void) => void;
}

function getConnectionInfo(): ConnectionInfoLike | null {
  if (typeof navigator === 'undefined') return null;
  const conn = (navigator as Navigator & { connection?: ConnectionInfoLike }).connection;
  return conn ?? null;
}

// ---------------------------------------------------------------------------
// Singleton + public API (mirrors syncStatusStore's module-level exports)
// ---------------------------------------------------------------------------

const connectionQualityMonitor = new ConnectionQualityMonitor();

export const recordRequestSample = (ms: number) =>
  connectionQualityMonitor.recordRequestSample(ms);
export const recordRequestFailure = () =>
  connectionQualityMonitor.recordRequestFailure();
export const getLinkQuality = (): LinkQuality =>
  connectionQualityMonitor.getQuality();
export const getLinkQualitySnapshot = (): LinkQualitySnapshot =>
  connectionQualityMonitor.getSnapshot();
export const subscribeLinkQuality = (listener: LinkQualityListener) =>
  connectionQualityMonitor.subscribe(listener);
export const resetConnectionQualityForTests = () =>
  connectionQualityMonitor.resetForTests();

/**
 * @process timedFetch
 * Steps: Time a fetch call; record latency on resolve, failure on reject.
 * Inputs: Same signature as window.fetch.
 * Outputs: The fetch Response, unmodified.
 * Failure Handling: Records the failure and re-throws — callers keep their
 *   existing error handling; this wrapper never swallows errors.
 * Idempotency: NOT REQUIRED — each call performs one request.
 */
export async function timedFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const startedAt = Date.now();
  try {
    const response = await fetch(input, init);
    recordRequestSample(Date.now() - startedAt);
    return response;
  } catch (err) {
    recordRequestFailure();
    throw err;
  }
}
